import * as React from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

export interface ConfirmOptions {
  title?: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Usa o estilo destrutivo no botão de confirmação (ex.: exclusões). */
  destructive?: boolean
}

interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Diálogo de confirmação na própria UI (substitui o window.confirm nativo).
 * Prefira usar o hook `useConfirm` para o padrão imperativo `await confirm(...)`.
 */
export function ConfirmDialog({
  open,
  title = "Tem certeza?",
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={destructive ? "destructive" : "default"}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

/**
 * Hook para confirmação imperativa via UI. Uso:
 *
 *   const { confirm, confirmDialog } = useConfirm()
 *   ...
 *   if (!(await confirm({ description: "Excluir?" , destructive: true }))) return
 *   ...
 *   return (<>{outroConteudo}{confirmDialog}</>)
 */
export function useConfirm() {
  const [state, setState] = React.useState<ConfirmState | null>(null)

  const confirm = React.useCallback(
    (options: ConfirmOptions = {}) =>
      new Promise<boolean>((resolve) => {
        setState({ ...options, resolve })
      }),
    []
  )

  const handleClose = React.useCallback(
    (result: boolean) => {
      setState((current) => {
        current?.resolve(result)
        return null
      })
    },
    []
  )

  const confirmDialog = (
    <ConfirmDialog
      open={state !== null}
      title={state?.title}
      description={state?.description}
      confirmLabel={state?.confirmLabel}
      cancelLabel={state?.cancelLabel}
      destructive={state?.destructive}
      onConfirm={() => handleClose(true)}
      onCancel={() => handleClose(false)}
    />
  )

  return { confirm, confirmDialog }
}
